import firebaseApp from "../firebase/credenciales";
import {
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";

const db = getFirestore(firebaseApp);

/**
 * REGLA ÚNICA DE ASIGNACIÓN DE SEDES
 * ---------------------------------------------------------------------------
 * Un trabajador tiene UN SOLO documento de coordenadas por sede, y su ID es
 * siempre el mismo: `${dni}_${sedeId}`.
 *
 * Con un ID determinista, dos caminos distintos (o dos pulsaciones, o dos
 * pantallas abiertas) escriben sobre el MISMO documento con `merge`, así que
 * el duplicado es imposible por construcción — no depende de que cada camino
 * recuerde comprobar antes de escribir, que es como se duplicó hasta ahora
 * (había tres esquemas de ID conviviendo: aleatorio, `${grupoId}_${sedeId}` y
 * `${sedeId}`).
 *
 * La procedencia (quién mantiene la asignación) se guarda en el propio
 * documento para que quitar un grupo NO borre una asignación individual:
 *   - `asignadoIndividual`: true si alguien la asignó a mano
 *   - `grupos`: [{ id, nombre }] grupos que la mantienen
 *   - `grupoId` / `grupoNombre`: etiqueta del grupo principal (compatibilidad)
 */

export const idCanonicoCoordenada = (dni, sedeId) =>
  `${String(dni || "")}_${String(sedeId || "")}`;

export const referenciaCoordenada = (dni, sedeId) =>
  doc(db, `asistencia/${String(dni || "")}/coordenadas`, idCanonicoCoordenada(dni, sedeId));

// Lee la procedencia de un documento, entendiendo también los documentos
// antiguos que solo tenían `grupoId`/`asignadoPorGrupo`
export const leerProcedencia = (data = {}) => {
  const grupos = Array.isArray(data.grupos)
    ? data.grupos
        .map((g) => ({
          id: String(g?.id || g?.grupoId || ""),
          nombre: String(g?.nombre || g?.grupoNombre || ""),
        }))
        .filter((g) => g.id)
    : [];

  const grupoId = String(data.grupoId || "");
  if (grupoId && !grupos.some((g) => g.id === grupoId)) {
    grupos.push({ id: grupoId, nombre: String(data.grupoNombre || "") });
  }

  const asignadoPorGrupo = data.asignadoPorGrupo === true || grupos.length > 0;
  // Un documento sin marcas es una asignación manual (botón "Aplicar")
  const asignadoIndividual =
    data.asignadoIndividual !== undefined ? data.asignadoIndividual === true : !asignadoPorGrupo;

  return { grupos, asignadoPorGrupo, asignadoIndividual };
};

// Datos de la sede que se copian a las coordenadas del trabajador
export const construirDatosCoordenada = ({ ruc, sede, companyName = "Mi Empresa" }) => ({
  id_sede_empresa: sede.id,
  id_empresa: ruc,
  nombre: sede.nombre,
  latitud: sede.latitud,
  longitud: sede.longitud,
  radio: sede.radio,
  activo: sede.activo !== undefined ? sede.activo : true,
  prioridad: 1,
  empresa: companyName,
  horarioIngreso: sede.horarioIngreso || "",
  horarioSalida: sede.horarioSalida || "",
  inicioRefrigerio: sede.inicioRefrigerio || "",
  finRefrigerio: sede.finRefrigerio || "",
  regimenId: sede.regimenId || "",
  regimenNombre: sede.regimenNombre || "",
  updatedAt: serverTimestamp(),
});

/**
 * Operación de asignación lista para un batch (sin lecturas).
 * La procedencia se acumula con arrayUnion, así que varios grupos pueden
 * mantener el mismo documento sin pisarse entre ellos.
 */
export const operacionAsignacionSede = ({
  dni,
  ruc,
  sede,
  companyName = "Mi Empresa",
  grupoId = null,
  grupoNombre = "",
  individual = false,
  crearMarcaDeFecha = false,
  idDocumento = null,
}) => {
  const grupoIdStr = String(grupoId || "");

  const data = {
    ...construirDatosCoordenada({ ruc, sede, companyName }),
    ultimoUso: serverTimestamp(),
    asignadoPorGrupo: Boolean(grupoIdStr),
  };

  if (grupoIdStr) {
    data.grupos = arrayUnion({ id: grupoIdStr, nombre: String(grupoNombre || "") });
    data.grupoId = grupoIdStr;
    data.grupoNombre = String(grupoNombre || "");
  }

  if (individual) {
    data.asignadoIndividual = true;
  }

  if (crearMarcaDeFecha) {
    data.createdAt = serverTimestamp();
  }

  const ref = idDocumento
    ? doc(db, `asistencia/${String(dni || "")}/coordenadas`, String(idDocumento))
    : referenciaCoordenada(dni, sede.id);

  return { ref, data };
};

/**
 * Asigna la sede a un trabajador escribiendo en el documento canónico.
 * Acumula la procedencia con lo que ya tuviera el documento.
 */
export const asignarSedeAUsuario = async ({
  dni,
  ruc,
  sede,
  companyName = "Mi Empresa",
  individual = false,
  grupoId = null,
  grupoNombre = "",
  extra = {},
}) => {
  const dniStr = String(dni || "");
  const sedeId = String(sede?.id || "");
  if (!dniStr || !sedeId || !ruc) return null;

  // Se busca el documento que ya existe para esta (DNI, sede) y se escribe
  // SOBRE ÉL, sea cual sea su ID: así ni se duplica ni se crea un documento
  // nuevo mientras queden documentos antiguos sin consolidar. Si no existe
  // ninguno, se usa el ID canónico.
  const existentes = await getDocs(
    query(
      collection(db, `asistencia/${dniStr}/coordenadas`),
      where("id_sede_empresa", "==", sedeId)
    )
  );

  const idCanonico = idCanonicoCoordenada(dniStr, sedeId);
  const docCanonico = existentes.docs.find((d) => d.id === idCanonico);
  const docObjetivo = docCanonico || existentes.docs[0] || null;

  const ref = docObjetivo ? docObjetivo.ref : referenciaCoordenada(dniStr, sedeId);
  const previo = docObjetivo ? docObjetivo.data() : {};
  const procedencia = leerProcedencia(previo);

  const grupos = [...procedencia.grupos];
  const grupoIdStr = String(grupoId || "");
  if (grupoIdStr && !grupos.some((g) => g.id === grupoIdStr)) {
    grupos.push({ id: grupoIdStr, nombre: String(grupoNombre || "") });
  }

  const principal = grupoIdStr
    ? { id: grupoIdStr, nombre: String(grupoNombre || "") }
    : grupos[0] || null;

  const data = {
    ...construirDatosCoordenada({ ruc, sede, companyName }),
    ...extra,
    ultimoUso: serverTimestamp(),
    asignadoIndividual: individual || procedencia.asignadoIndividual,
    asignadoPorGrupo: grupos.length > 0,
    grupos,
    grupoId: principal?.id || "",
    grupoNombre: principal?.nombre || "",
  };

  if (!docObjetivo) {
    data.createdAt = serverTimestamp();
  }

  await setDoc(ref, data, { merge: true });

  return { id: ref.id, creado: !docObjetivo, ref, previo };
};

/**
 * Quita la sede a un trabajador.
 * - Con `grupoId`: quita solo la procedencia de ese grupo. El documento se
 *   borra únicamente si ninguna otra procedencia lo mantiene (individual u
 *   otro grupo); si no, se actualiza.
 * - Sin `grupoId`: quita todas las procedencias, por lo que acaba borrando el
 *   documento (equivale a "quitar al trabajador de la sede").
 * Recorre TODOS los documentos del trabajador para esa sede, así que también
 * limpia los que quedaron con IDs antiguos.
 */
export const quitarSedeDeUsuario = async ({ dni, sedeId, grupoId = null }) => {
  const dniStr = String(dni || "");
  const sedeIdStr = String(sedeId || "");
  if (!dniStr || !sedeIdStr) return { eliminados: 0, actualizados: 0 };

  const grupoIdStr = String(grupoId || "");

  const snap = await getDocs(
    query(
      collection(db, `asistencia/${dniStr}/coordenadas`),
      where("id_sede_empresa", "==", sedeIdStr)
    )
  );

  let eliminados = 0;
  let actualizados = 0;

  for (const coordDoc of snap.docs) {
    const procedencia = leerProcedencia(coordDoc.data() || {});
    const grupos = grupoIdStr
      ? procedencia.grupos.filter((g) => g.id !== grupoIdStr)
      : [];
    const sinProcedencia = grupos.length === 0 && !procedencia.asignadoIndividual;

    if (sinProcedencia) {
      await deleteDoc(coordDoc.ref);
      eliminados++;
      continue;
    }

    const principal = grupos[0] || null;
    await updateDoc(coordDoc.ref, {
      grupos,
      grupoId: principal?.id || "",
      grupoNombre: principal?.nombre || "",
      asignadoPorGrupo: grupos.length > 0,
      updatedAt: serverTimestamp(),
    });
    actualizados++;
  }

  return { eliminados, actualizados };
};

/**
 * SOLO LECTURA: cuántos documentos tiene el trabajador para esa sede (1 = ok,
 * >1 = quedan duplicados de esquemas antiguos pendientes de consolidar).
 */
export const contarDocumentosDeSede = async (dni, sedeId) => {
  const dniStr = String(dni || "");
  const sedeIdStr = String(sedeId || "");
  if (!dniStr || !sedeIdStr) return 0;

  const snap = await getDocs(
    query(
      collection(db, `asistencia/${dniStr}/coordenadas`),
      where("id_sede_empresa", "==", sedeIdStr)
    )
  );
  return snap.size;
};

/**
 * SOLO LECTURA: lista de trabajadores con más de un documento para la misma
 * sede. Sirve para medir/auditar los duplicados que siguen en Firestore.
 */
export const listarDuplicadosDeUsuario = async (dni) => {
  const dniStr = String(dni || "");
  if (!dniStr) return [];

  const snap = await getDocs(collection(db, `asistencia/${dniStr}/coordenadas`));
  const porSede = new Map();

  snap.forEach((coordDoc) => {
    const sedeId = String(coordDoc.data()?.id_sede_empresa || "");
    if (!sedeId) return;
    if (!porSede.has(sedeId)) porSede.set(sedeId, []);
    porSede.get(sedeId).push(coordDoc.id);
  });

  return [...porSede.entries()]
    .filter(([, ids]) => ids.length > 1)
    .map(([sedeId, ids]) => ({ sedeId, total: ids.length, ids }));
};
